import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { Row as TRow, RowData } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { BodyCell } from '../../components/BodyCell';
import { TableRow } from '../../components/TableRow';
import { getTableColumnDef } from '../../internal/getTableColumnDef';
import { StopPropagation } from '../../internal/StopPropagation';
import { ExpandButton } from './ExpandButton';

function getCellStyle(first: boolean, depth: number, theme: Theme) {
  return first ? { paddingLeft: theme.spacing(depth * 2) } : {};
}

interface RowProps<TData extends RowData> {
  row: TRow<TData>;
  isHighlight?: boolean;
  isClickable?: boolean;
  onClick?: (row: TRow<TData>) => void;
}

export const ExpandableRow = <TData extends RowData>({
  row,
  isClickable = false,
  isHighlight = false,
  onClick,
}: RowProps<TData>) => {
  const theme = useTheme();

  return (
    <TableRow
      isClickable={isClickable}
      isHighlight={isHighlight}
      onClick={() => onClick?.(row)}
    >
      {row.getVisibleCells().map((cell, index) => {
        const first = index === 0;
        const columnDef = getTableColumnDef(cell.column);
        const content = cell.getIsPlaceholder()
          ? null
          : flexRender(cell.column.columnDef.cell, cell.getContext());

        return (
          <BodyCell
            key={cell.id}
            padding={columnDef.cellPadding}
            style={getCellStyle(first, row.depth, theme)}
          >
            {first ? (
              <Box display="flex" alignItems="center">
                <StopPropagation>
                  <ExpandButton
                    expanded={row.getIsExpanded()}
                    hidden={!row.getCanExpand()}
                    onClick={row.getToggleExpandedHandler()}
                    margin="dense"
                  />
                </StopPropagation>
                {content}
              </Box>
            ) : (
              content
            )}
          </BodyCell>
        );
      })}
    </TableRow>
  );
};
